import { initTsrReactQuery } from '@ts-rest/react-query/v5'
import { useRecoilState } from 'recoil'
import { contract } from '../../../../server/src/tsr-contracts'
import { tokenState } from '../../store/atoms/token'
import { QueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

/**
 * Configuration options for the ts-rest client
 */
interface TsRestClientOptions {
  baseUrl?: string
  queryClient?: QueryClient
  enableLogs?: boolean
}

/**
 * Default configuration for the ts-rest client
 */
const defaultOptions: TsRestClientOptions = {
  baseUrl: '',
  enableLogs: process.env.NODE_ENV !== 'production',
}

/**
 * Hook to create a ts-rest client with authentication
 * @param options - Configuration options for the client
 * @returns A configured ts-rest client for all API contracts
 */
export const useTsrClient = (options: TsRestClientOptions = {}) => {
  const [idToken] = useRecoilState(tokenState)
  const mergedOptions = { ...defaultOptions, ...options }

  // Create authorization header with token if available
  // Use empty string as fallback to ensure it's always a string type
  const authHeader: string =
    idToken && idToken.__raw ? `Bearer ${idToken.__raw}` : ''

  // Memoize the client to prevent re-creation on every render
  // Only recreate when auth token or options change
  const client = useMemo(() => {
    try {
      // Get version from environment variable (injected at build time)
      const clientVersion = import.meta.env.VITE_PACKAGE_VERSION || 'unknown'

      return initTsrReactQuery(contract, {
        baseUrl: mergedOptions.baseUrl ?? '/',
        baseHeaders: {
          authorization: authHeader,
          'X-Client-Version': clientVersion,
        },
        queryClient: mergedOptions.queryClient,
      })
    } catch (error) {
      console.error('Failed to initialize ts-rest client', { error })
      throw error
    }
  }, [authHeader, mergedOptions.baseUrl, mergedOptions.queryClient])

  return client
}

/**
 * Hook to get a specific API client with proper authentication
 * @param contractPart - The specific part of the contract to use
 * @param options - Configuration options for the client
 * @returns A configured ts-rest client for the specified contract part
 */
export function useApiClient<T extends keyof typeof contract>(
  contractPart: T,
  options: TsRestClientOptions = {},
) {
  const client = useTsrClient(options)
  return client[contractPart]
}

/**
 * Hook to get the campaign client
 * @param options - Configuration options for the client
 * @returns The campaign client
 */
export const useCampaignClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('campaign', options)
}

/**
 * Hook to get the incentive client
 * @param options - Configuration options for the client
 * @returns The incentive client
 */
export const useIncentiveClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('incentive', options)
}

/**
 * Hook to get the SKU code client
 * @param options - Configuration options for the client
 * @returns The SKU code client
 */
export const useSkuCodeClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('skuCode', options)
}

/**
 * Hook to get the end users dashboard client
 * @param options - Configuration options for the client
 * @returns The end users dashboard client
 */
export const useEndUsersDashboardClient = (
  options: TsRestClientOptions = {},
) => {
  return useApiClient('endUsers', options)
}

/**
 * Hook to get the holiday client
 * @param options - Configuration options for the client
 * @returns The holiday client
 */
export const useHolidayClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('holiday', options)
}

/**
 * Hook to get the intersolve client
 * @param options - Configuration options for the client
 * @returns The intersolve client
 */
export const useIntersolveClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('intersolve', options)
}

/**
 * Hook to get the Intersolve Classic client (admin cancel endpoints)
 * @param options - Configuration options for the client
 * @returns The Intersolve Classic client
 */
export const useIntersolveClassicClient = (
  options: TsRestClientOptions = {},
) => {
  return useApiClient('intersolveClassic', options)
}

/**
 * Hook to get the end users integrations client
 * @param options - Configuration options for the client
 * @returns The end users integrations client
 */
export const useEndUsersIntegrationsClient = (
  options: TsRestClientOptions = {},
) => {
  return useApiClient('endUsersIntegrations', options)
}

/**
 * Hook to get the admin client
 * @param options - Configuration options for the client
 * @returns The admin client
 */
export const useAdminClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('admin', options)
}

/**
 * Hook to get the user client
 * @param options - Configuration options for the client
 * @returns The user client
 */
export const useUserClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('user', options)
}

/**
 * Hook to get the dashboard client
 * @param options - Configuration options for the client
 * @returns The dashboard client
 */
export const useDashboardClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('dashboard', options)
}

/**
 * Hook to get the customer client
 * @param options - Configuration options for the client
 * @returns The customer client
 */
export const useCustomerClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('customer', options)
}

export const usePlaygroundClient = (options: TsRestClientOptions = {}) => {
  return useApiClient('playground', options)
}
